const libAssociationEditor = require('pict-section-recordset/source/views/associate/RecordSet-AssociationEditor.js');

/**
 * MaterialOrganizationAssociationEditor — the "Organizations" tab on a Material read view (and the
 * reciprocal association manager for material↔organization relationships, the OrganizationMaterialJoin).
 *
 * The base RecordSet-AssociationEditor already flags a relationship whose join has a past ExpirationDate
 * (or a future EffectiveDate) with a read-only status badge via its zero-config _computeStatusBadge. This
 * subclass adds the missing WRITE side the task asks for: an inline `<input type="date">` on each row that
 * SETS, UPDATES or BACKFILLS the join's ExpirationDate. The editable column is declared on the association
 * (JoinEditableFields, Type:'date' — see PlatformConfiguration-Application-Web.js); everything else — the
 * add picker, remove button, the expired badge — is inherited unchanged.
 *
 * Why a subclass rather than config alone: the base editor's inline edit controls only know the
 * checkbox / number / select / text kinds, so a `date` field is invisible to it. Teaching it the kind
 * needs (a) a `_buildEditFields` that shapes a date row and (b) an edit-field template that emits the date
 * input. The base registers its templates GLOBALLY by hash (pict.TemplateProvider is last-writer-wins) and
 * other association tabs (e.g. Project↔ObservationManifest) share those PRSP-AssociationEditor-* hashes, so
 * this view renders from a PRIVATE, re-namespaced COPY of the base templates — it cannot clobber them, and
 * it stays self-contained even on a page where no base editor ever initialized.
 *
 * The read view instantiates the association tab's editor only if one is not already registered under the
 * hash `RSP-AssocEditor-<RecordSet>-<Association>` (see RecordSet-Read._buildValidTabs), so pre-registering
 * this subclass under `RSP-AssocEditor-Material-MaterialOrganization` makes the Material tab use it.
 */

const _BASE = libAssociationEditor.default_configuration;

// Rewrite the base editor's own template + renderable hashes into a private namespace, leaving every other
// tag (icons, {~RecordCard:~}, the .prsp-assoc-* CSS classes, Record.* data refs) untouched.
const fNamespace = (pString) => String(pString)
	.split('PRSP_Renderable_AssociationEditor').join('HLMaterialOrgAssoc_Renderable')
	.split('PRSP-AssociationEditor').join('HLMaterialOrgAssoc');

// A private copy of every base template under the namespaced hashes, then: teach the edit-field dispatcher
// a `date` branch and add the date-input template it dispatches to.
const _NamespacedTemplates = _BASE.Templates.map((pTemplate) => ({ Hash: fNamespace(pTemplate.Hash), Template: fNamespace(pTemplate.Template) }));
for (let i = 0; i < _NamespacedTemplates.length; i++)
{
	if (_NamespacedTemplates[i].Hash === 'HLMaterialOrgAssoc-EditField')
	{
		_NamespacedTemplates[i].Template += `{~TIfAbs:HLMaterialOrgAssoc-EditDate:Record:Record.IsDate^TRUE^~}`;
	}
}
_NamespacedTemplates.push(
	{
		Hash: 'HLMaterialOrgAssoc-EditDate',
		Template: /*html*/`<label class="prsp-assoc-cfg">{~D:Record.Label~}<input type="date" class="prsp-assoc-cfg-date" value="{~D:Record.Value~}" title="Set, update or clear the relationship expiration date" onchange="_Pict.views['{~D:Record.ViewHash~}'].setExpiration({~D:Record.JoinID~},this.value)" /></label>`
	});

const _NamespacedRenderables = (_BASE.Renderables || []).map((pRenderable) =>
	Object.assign({}, pRenderable, { RenderableHash: fNamespace(pRenderable.RenderableHash), TemplateHash: fNamespace(pRenderable.TemplateHash) }));

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION_MaterialOrgAssociationEditor = Object.assign({}, _BASE,
	{
		ViewIdentifier: 'RSP-AssocEditor-Material-MaterialOrganization',
		DefaultRenderable: fNamespace(_BASE.DefaultRenderable),
		Templates: _NamespacedTemplates,
		Renderables: _NamespacedRenderables,
		// Reuse the base widget CSS (same .prsp-assoc-* classes the namespaced templates still use, injected
		// idempotently) and add the one rule for the date control.
		CSS: `${ _BASE.CSS }
		.prsp-assoc-cfg-date { font: inherit; font-size: 0.8rem; padding: 0.1rem 0.3rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 5px; }
		`,
	});

class viewMaterialOrganizationAssociationEditor extends libAssociationEditor
{
	/**
	 * Shape each editable join field into per-row render data. Adds the `date` kind the base does not know
	 * (formatting the stored datetime down to the yyyy-mm-dd an `<input type="date">` expects); every other
	 * kind defers to the base's shaping unchanged.
	 * @param {Array<Record<string, any>>} pFields @param {Record<string, any>} pItem
	 * @return {Array<Record<string, any>>}
	 */
	_buildEditFields(pFields, pItem)
	{
		if (!Array.isArray(pFields) || pFields.length < 1)
		{
			return [];
		}
		return pFields.map((pField) =>
		{
			if (pField && (pField.Type === 'date'))
			{
				const tmpJoin = pItem.JoinRecord || {};
				return {
					ViewHash: this.Hash,
					JoinID: pItem.JoinID,
					Field: pField.Field,
					Label: pField.Label || pField.Field,
					IsCheckbox: false,
					IsNumber: false,
					IsSelect: false,
					IsText: false,
					IsDate: true,
					Value: this._toDateInputValue(tmpJoin[pField.Field]),
					Options: [],
				};
			}
			// Reuse the base single-field shaping for checkbox / number / select / text.
			return super._buildEditFields([ pField ], pItem)[0];
		});
	}

	/**
	 * Reduce a stored datetime to the `yyyy-mm-dd` an `<input type="date">` needs. Reads the date part off
	 * the string directly (no Date parse, so no timezone day-shift); an unset / zero / non-date value maps
	 * to '' so a relationship with no expiration shows an empty control the user can fill in (the "add
	 * missing expiration info" case).
	 * @param {any} pValue
	 * @return {string}
	 */
	_toDateInputValue(pValue)
	{
		if (!pValue)
		{
			return '';
		}
		const tmpMatch = String(pValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (!tmpMatch || (tmpMatch[1] === '0000'))
		{
			return '';
		}
		return `${ tmpMatch[1] }-${ tmpMatch[2] }-${ tmpMatch[3] }`;
	}

	/**
	 * Persist a set / updated / cleared expiration on one relationship's join row, then repaint from the
	 * server so the read-only Expired / Not-yet-effective badge reflects the new date immediately (the
	 * association manager clears its cached join list on the write, so the reload is fresh).
	 * @param {string|number} pJoinID @param {string} pValue - the date input value (yyyy-mm-dd, or '' to clear).
	 * @return {Promise<void>}
	 */
	async setExpiration(pJoinID, pValue)
	{
		const tmpItem = this._lastItems.find((pItem) => String(pItem.JoinID) === String(pJoinID));
		if (!tmpItem || !tmpItem.JoinRecord)
		{
			return;
		}
		try
		{
			await this.manager.updateJoin(this.options.AssociationHash, tmpItem.JoinRecord,
				{ ExpirationDate: ((pValue === undefined) || (pValue === null)) ? '' : pValue });
		}
		catch (pError)
		{
			this.pict.log.error(`MaterialOrganizationAssociationEditor [${ this.Hash }]: failed to save ExpirationDate for join ${ pJoinID }.`, pError);
			this._toast('Could not save the expiration date.', 'error');
		}
		await this.renderEditor();
	}
}

module.exports = viewMaterialOrganizationAssociationEditor;

module.exports.default_configuration = _DEFAULT_CONFIGURATION_MaterialOrgAssociationEditor;
